import React, { useState } from "react";
import { X, Plus, Check } from "lucide-react";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const TopicChip = ({ topic, isAdded, onAdd }) => {
  const [{ isDragging }, drag] = useDrag({
    type: "TOPIC",
    item: { topic },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  return (
    <div
      ref={drag}
      onClick={() => !isAdded && onAdd(topic)}
      className={`gradient-ring flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium select-none transition-all duration-200 ${
        isAdded
          ? "is-active border border-transparent bg-gradient-to-r from-pink-500/20 to-blue-500/20 dark:text-white text-textPrimary opacity-70 cursor-default"
          : "dark:glass-effect bg-bgSecondary border border-white/10 dark:text-gray-300 text-textSecondary hover:text-textPrimary cursor-pointer"
      } ${isDragging ? "opacity-40 scale-95" : ""}`}
    >
      {isAdded ? <Check size={11} /> : <Plus size={11} />}
      {topic}
    </div>
  );
};

const DropZoneOverlay = ({ onDrop }) => {
  const [{ isOver }, drop] = useDrop({
    accept: "TOPIC",
    drop: (item) => onDrop(item.topic),
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  return (
    <div
      ref={drop}
      className={`border-2 border-dashed rounded-xl px-4 py-3 text-center text-xs transition-all duration-200 ${
        isOver
          ? "border-blue-400/60 bg-gradient-to-r from-pink-500/10 to-blue-500/10 dark:text-white text-textPrimary"
          : "border-white/10 dark:text-gray-500 text-textSecondary"
      }`}
    >
      {isOver ? "Drop to add topic" : "Or drag topics here"}
    </div>
  );
};

const SelectedTopicRow = ({ topic, count, onCountChange, onRemove }) => (
  <div className="flex items-center gap-3 p-3 rounded-xl dark:glass-effect bg-bgSecondary border border-white/10">
    <span className="flex-1 text-sm dark:text-gray-200 text-textPrimary font-medium truncate">
      {topic}
    </span>
    <div className="flex items-center gap-1.5 shrink-0">
      <button
        onClick={() => onCountChange(topic, Math.max(0, count - 1))}
        className="gradient-ring w-6 h-6 rounded-full border border-white/20 dark:text-gray-400 text-textSecondary hover:text-blue-400 flex items-center justify-center text-xs transition-colors"
      >
        −
      </button>
      <span className="w-6 text-center text-sm dark:text-white text-textPrimary font-semibold">
        {count}
      </span>
      <button
        onClick={() => onCountChange(topic, count + 1)}
        className="gradient-ring w-6 h-6 rounded-full border border-white/20 dark:text-gray-400 text-textSecondary hover:text-blue-400 flex items-center justify-center text-xs transition-colors"
      >
        +
      </button>
    </div>
    <button
      onClick={() => onRemove(topic)}
      className="p-1 rounded-lg hover:bg-red-500/10 text-red-400 transition-colors shrink-0"
    >
      <X size={13} />
    </button>
  </div>
);

const AIStep2_Topics = ({
  suggestedTopics,
  topics,
  questionsPerTopic,
  onAddTopic,
  onRemoveTopic,
  onCountChange,
  onGenerate,
  isGenerating,
}) => {
  const [inputValue, setInputValue] = useState("");

  const handleAddCustom = () => {
    const t = inputValue.trim();
    if (t) { onAddTopic(t); setInputValue(""); }
  };

  const totalQuestions = Object.values(questionsPerTopic).reduce((s, n) => s + (n || 0), 0);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col lg:flex-row gap-6 w-full">
        {/* Left: Suggested topics pool */}
        <div className="flex-1 space-y-5">
          <div className="rounded-2xl border border-white/10 bg-bgSecondary dark:glass-effect p-5 space-y-4">
            <div>
              <h3 className="text-sm font-semibold dark:text-white text-textPrimary">
                AI Suggested Topics
              </h3>
              <p className="text-xs dark:text-gray-400 text-textSecondary mt-0.5">
                Click a topic to add it, or drag it to the panel.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {suggestedTopics.map((topic) => (
                <TopicChip
                  key={topic}
                  topic={topic}
                  isAdded={topics.includes(topic)}
                  onAdd={onAddTopic}
                />
              ))}
            </div>

            <DropZoneOverlay onDrop={onAddTopic} />
          </div>

          {/* Custom topic input */}
          <div className="rounded-2xl border border-white/10 bg-bgSecondary dark:glass-effect p-4 space-y-2">
            <p className="text-xs font-medium dark:text-gray-400 text-textSecondary">
              Add a custom topic
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddCustom()}
                placeholder="Type a topic name..."
                className="gradient-ring dark:glass-effect bg-bgPrimary dark:!text-white !text-textPrimary border border-white/10 rounded-xl flex-1 px-4 py-2.5 text-sm outline-none transition-colors"
              />
              <button
                onClick={handleAddCustom}
                className="button px-4 py-2 flex items-center gap-1.5 text-sm"
              >
                <Plus size={14} />
                <span>Add</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right: Selected topics sidebar */}
        <div className="lg:w-72 shrink-0">
          <div className="rounded-2xl border border-white/10 bg-bgSecondary dark:glass-effect p-5 space-y-4 sticky top-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold dark:text-white text-textPrimary">
                Selected Topics
              </h3>
              <span className="gradient-ring is-active text-xs px-2 py-0.5 rounded-full border border-transparent bg-gradient-to-r from-pink-500/20 to-blue-500/20 dark:text-white text-textPrimary">
                {topics.length}
              </span>
            </div>

            {topics.length === 0 ? (
              <p className="text-xs dark:text-gray-500 text-textSecondary text-center py-4">
                No topics selected yet. Click or drag topics from the pool.
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {topics.map((topic) => (
                  <SelectedTopicRow
                    key={topic}
                    topic={topic}
                    count={questionsPerTopic[topic] || 0}
                    onCountChange={onCountChange}
                    onRemove={onRemoveTopic}
                  />
                ))}
              </div>
            )}

            {topics.length > 0 && (
              <div className="space-y-3 pt-2 border-t border-white/10">
                <div className="flex items-center justify-between text-xs">
                  <span className="dark:text-gray-400 text-textSecondary">Total questions</span>
                  <span className="font-bold dark:text-white text-textPrimary">{totalQuestions}</span>
                </div>
                <button
                  onClick={onGenerate}
                  disabled={isGenerating || totalQuestions === 0}
                  className="button w-full flex items-center justify-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <span>Generate Questions →</span>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export default AIStep2_Topics;
